import React, { useState } from 'react';
import { ExternalLink, Plus, Trash2, Bookmark, Wand2, Sparkles } from "lucide-react";
import PageHeader from './PageHeader';
import PaletteDisplay from './PaletteDisplay';
import CompactExportDropdown from './CompactExportDropdown';
import MoodBuilder from './MoodBuilder';
import { hexToHsl, hslToHex } from '../lib/colorUtils';

const PaletteBuilder = ({
    selectedColor,
    onColorClick,
    visibleFields = [],
    colorBlindnessType = 'none',
    isDark = false,
    addColorToPalette,
    setVisualizerColors,
    savePalette,
    builderPalette = [],
    setBuilderPalette,
    addColorToCustomPalette
}) => {
    const [activeTab, setActiveTab] = useState('manual'); // 'manual' or 'mood'

    const addColorToPaletteBuilder = (type) => {
        if (!selectedColor) return;

        const [h, s, l] = hexToHsl(selectedColor);
        let newColor = selectedColor;

        // Helper to ensure values stay in valid ranges
        const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

        // Add slight randomness for variations (allows multiple clicks to work)
        const randomVariation = () => Math.random() * 6 - 3; // -3 to +3 random variation
        const smallRandomVariation = () => Math.random() * 3 - 1.5; // -1.5 to +1.5 for smaller changes

        // Helper to create color variations with optional randomness
        const createVariation = (hue, saturation, lightness, addRandomness = true) => {
            const finalHue = addRandomness ? hue + smallRandomVariation() : hue;
            const finalSat = addRandomness ? saturation + randomVariation() : saturation;
            const finalLight = addRandomness ? lightness + randomVariation() : lightness;

            return hslToHex(
                ((finalHue % 360) + 360) % 360,
                clamp(finalSat, 5, 95),
                clamp(finalLight, 5, 95)
            );
        };

        switch (type) {
            case 'base':
                newColor = createVariation(h, s, l, false); // Base color without randomness
                break;
            case 'light':
                newColor = createVariation(h, s, Math.min(l + 20, 90));
                break;
            case 'lighter':
                newColor = createVariation(h, s, Math.min(l + 35, 90));
                break;
            case 'dark':
                newColor = createVariation(h, s, Math.max(l - 20, 10));
                break;
            case 'darker':
                newColor = createVariation(h, s, Math.max(l - 35, 10));
                break;
            case 'shade':
                newColor = createVariation(h, Math.max(s - 10, 10), Math.max(l - 25, 10));
                break;
            case 'tint':
                newColor = createVariation(h, Math.max(s - 15, 10), Math.min(l + 30, 90));
                break;
            case 'highlight':
                newColor = createVariation(h, Math.min(s + 20, 95), Math.min(l + 25, 85));
                break;
            case 'complementary':
                newColor = createVariation((h + 180) % 360, s, l);
                break;
            case 'complementary-light':
                newColor = createVariation((h + 180) % 360, s, Math.min(l + 25, 85));
                break;
            case 'complementary-dark':
                newColor = createVariation((h + 180) % 360, s, Math.max(l - 25, 15));
                break;
            case 'triadic1':
                newColor = createVariation((h + 120) % 360, s, l);
                break;
            case 'triadic2':
                newColor = createVariation((h + 240) % 360, s, l);
                break;
            case 'analogous-warm':
                newColor = createVariation((h + 30) % 360, s, l);
                break;
            case 'analogous-cool':
                newColor = createVariation((h - 30 + 360) % 360, s, l);
                break;
            case 'split-comp1':
                newColor = createVariation((h + 150) % 360, s, l);
                break;
            case 'split-comp2':
                newColor = createVariation((h + 210) % 360, s, l);
                break;
            case 'saturated':
                newColor = createVariation(h, Math.min(s + 25, 95), l);
                break;
            case 'desaturated':
                newColor = createVariation(h, Math.max(s - 25, 5), l);
                break;
            case 'warm':
                newColor = createVariation((h - 15 + 360) % 360, Math.min(s + 10, 90), l);
                break;
            case 'cool':
                newColor = createVariation((h + 15) % 360, Math.min(s + 10, 90), l);
                break;
            default:
                console.warn(`Unknown color variation type: ${type}`);
                newColor = selectedColor;
                break;
        }

        // Always add the color (allow duplicates/variations)
        setBuilderPalette(prev => [...prev, newColor]);
    };

    const clearBuilderPalette = () => {
        setBuilderPalette([]);
    };

    const removeColorFromBuilderPalette = (colorToRemove) => {
        setBuilderPalette(prev => prev.filter(color => color !== colorToRemove));
    };

    const handleExportToVisualizer = (colors, paletteName, event) => {
        const colorObjects = colors.map((color, index) => ({
            id: `${paletteName.toLowerCase().replace(/\s+/g, '-')}-${index}`,
            value: color
        }));

        setVisualizerColors(colorObjects, event.target);
    };

    // Generate CSS for palette
    const generatePaletteCss = (colors, paletteName) => {
        const cssVarName = paletteName.toLowerCase().replace(/\s+/g, '-');
        return `:root {\n${colors.map((color, i) => `  --${cssVarName}-${i + 1}: ${color};`).join('\n')}\n}\n\n/* Usage examples */\n.${cssVarName}-primary { color: var(--${cssVarName}-1); }\n.${cssVarName}-bg { background-color: var(--${cssVarName}-1); }`;
    };

    // Generate SVG for palette
    const generatePaletteSvg = (colors, paletteName) => {
        const width = colors.length * 60;
        const height = 80;

        return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <!-- ${paletteName} Palette -->
  <rect width="${width}" height="${height}" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8"/>
  ${colors.map((color, i) => `
  <rect x="${i * 60 + 10}" y="10" width="50" height="50" fill="${color}" stroke="#fff" stroke-width="2" rx="4"/>
  <text x="${i * 60 + 35}" y="72" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="#666">${color}</text>`).join('')}
</svg>`;
    };

    return (
        <div className="overflow-visible">
            <PageHeader
                title="Palette Builder"
                description="Build custom color palettes with interactive variations"
            />

            {/* Tab Switcher */}
            <div className="mb-6">
                <nav className="flex space-x-6 overflow-x-auto scrollbar-thin border-b border-gray-200 dark:border-gray-700" aria-label="Builder Tabs">
                    <button
                        onClick={() => setActiveTab('manual')}
                        className={`
                            whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center gap-2 -mb-px
                            ${activeTab === 'manual'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                            }
                        `}
                        aria-current={activeTab === 'manual' ? 'page' : undefined}
                    >
                        <Wand2 className="w-4 h-4" />
                        Manual Builder
                    </button>
                    <button
                        onClick={() => setActiveTab('mood')}
                        className={`
                            whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center gap-2 -mb-px
                            ${activeTab === 'mood'
                                ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                            }
                        `}
                        aria-current={activeTab === 'mood' ? 'page' : undefined}
                    >
                        <Sparkles className="w-4 h-4" />
                        Mood Builder
                    </button>
                </nav>
            </div>

            {/* Mood Builder Tab */}
            {activeTab === 'mood' && (
                <MoodBuilder
                    onColorClick={onColorClick}
                    visibleFields={visibleFields}
                    colorBlindnessType={colorBlindnessType}
                    isDark={isDark}
                    addColorToPalette={addColorToPalette}
                    setVisualizerColors={setVisualizerColors}
                    savePalette={savePalette}
                    addColorToCustomPalette={addColorToCustomPalette}
                />
            )}

            {/* Manual Builder Tab */}
            {activeTab === 'manual' && !selectedColor ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <div className="mb-4">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Plus className="w-8 h-8" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">Start Building</h3>
                        <p className="text-sm">Select a base color from the toolbar to start building your custom palette.</p>
                    </div>
                </div>
            ) : activeTab === 'manual' ? (
                <div className="space-y-6 overflow-visible">
                    {/* Base Color Display */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-3">
                            <div
                                className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: selectedColor }}
                            />
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Base Color</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{selectedColor}</p>
                            </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                            Use the buttons below to add colors to your custom palette.
                        </p>
                    </div>

                    {/* Color Action Buttons */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Add Colors to Palette
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Build your palette using variations and harmonies
                            </p>
                        </div>
                        <div className="p-4">
                            {/* Basic Variations */}
                            <div className="mb-6">
                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Basic Variations</h5>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { key: 'base', label: '+ Base Color' },
                                    { key: 'lighter', label: '+ Lighter' },
                                    { key: 'light', label: '+ Light' },
                                    { key: 'dark', label: '+ Dark' },
                                    { key: 'darker', label: '+ Darker' },
                                    { key: 'shade', label: '+ Shade' },
                                    { key: 'tint', label: '+ Tint' },
                                    { key: 'highlight', label: '+ Highlight' }
                                ].map(button => (
                                    <button
                                        key={button.key}
                                        onClick={() => addColorToPaletteBuilder(button.key)}
                                        className="px-3 py-2 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                                    >
                                        {button.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Harmony Colors */}
                        <div className="mb-6">
                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Harmony Colors</h5>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { key: 'complementary', label: '+ Complementary' },
                                    { key: 'complementary-light', label: '+ Comp. Light' },
                                    { key: 'complementary-dark', label: '+ Comp. Dark' },
                                    { key: 'triadic1', label: '+ Triadic 1' },
                                    { key: 'triadic2', label: '+ Triadic 2' },
                                    { key: 'analogous-warm', label: '+ Analogous Warm' },
                                    { key: 'analogous-cool', label: '+ Analogous Cool' },
                                    { key: 'split-comp1', label: '+ Split Comp 1' },
                                    { key: 'split-comp2', label: '+ Split Comp 2' }
                                ].map(button => (
                                    <button
                                        key={button.key}
                                        onClick={() => addColorToPaletteBuilder(button.key)}
                                        className="px-3 py-2 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                    >
                                        {button.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Accent Colors */}
                        <div className="mb-4">
                            <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Accent Colors</h5>
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { key: 'saturated', label: '+ Saturated' },
                                    { key: 'desaturated', label: '+ Desaturated' },
                                    { key: 'warm', label: '+ Warm Tone' },
                                    { key: 'cool', label: '+ Cool Tone' }
                                ].map(button => (
                                    <button
                                        key={button.key}
                                        onClick={() => addColorToPaletteBuilder(button.key)}
                                        className="px-3 py-2 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                    >
                                        {button.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        </div>
                    </div>

                    {/* Builder Palette Display */}
                    {builderPalette.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            Your Builder Palette
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {builderPalette.length} colors
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={clearBuilderPalette}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                            Clear
                                        </button>
                                        <button
                                            onClick={() => savePalette('Builder Palette', builderPalette)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                        >
                                            <Bookmark className="w-3 h-3" />
                                            Save
                                        </button>
                                        <button
                                            onClick={(event) => handleExportToVisualizer(builderPalette, 'Builder Palette', event)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                        >
                                            <ExternalLink className="w-3 h-3" />
                                            Visualizer
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <PaletteDisplay
                                    colors={builderPalette}
                                    onColorClick={onColorClick}
                                    baseColor={selectedColor}
                                    visibleFields={visibleFields}
                                    colorBlindnessType={colorBlindnessType}
                                    isDark={isDark}
                                    addColorToPalette={addColorToPalette}
                                    addColorToCustomPalette={addColorToCustomPalette}
                                    removeColorFromPalette={removeColorFromBuilderPalette}
                                />
                            </div>
                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                                <CompactExportDropdown
                                    colors={builderPalette}
                                    paletteName="Builder Palette"
                                    getCss={() => generatePaletteCss(builderPalette, 'Builder Palette')}
                                    getSvg={() => generatePaletteSvg(builderPalette, 'Builder Palette')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tips */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                            💡 Builder Tips
                        </h4>
                        <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                            <li>• Click any button multiple times to add variations</li>
                            <li>• Build palettes with 3-8 colors for best results</li>
                            <li>• Mix basic variations with harmony colors for depth</li>
                            <li>• Use highlights and shades to create contrast</li>
                            <li>• Duplicates are automatically filtered out</li>
                        </ul>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default PaletteBuilder;
